import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
  apiGetMe,
  apiLogin,
  apiLogout,
  apiResendLoginCode,
  apiVerifyLoginCode,
} from "api/client";
import { organizationsApi } from "api/endpoints";
import { clearStoredTokens, getStoredAccessToken } from "services/tokenStorage";

/** Clave en localStorage (los JWT siguen en `tokenStorage.js`). */
export const AUTH_PERSIST_KEY = "fe-auth";

/**
 * Resuelve usuario + organización a partir de la respuesta de login/verify-code.
 * Si el backend no incluyó el usuario, lo completa con /me + listado de orgs.
 */
async function resolveSessionFromData(data) {
  let user = data?.user || null;
  let auth = data?.auth || null;
  let currentOrganization = data?.current_organization || null;
  // Rol del usuario en la org actual (owner/admin/contador/viewer). Viene en el
  // login (top-level) o en el objeto auth de /me. Se usa para adaptar la UI.
  let role = data?.role ?? data?.auth?.role ?? null;
  // El login/verify NO trae el objeto `auth` (scopes/role/restrict_modo). Si
  // falta user O auth, completamos con /me para tener SIEMPRE role+scopes al
  // instante (si no, la UI del contador no se adapta hasta un F5).
  if (!user || !auth) {
    const me = await apiGetMe();
    user = user || me?.user || null;
    auth = auth || me?.auth || null;
    role = role ?? me?.auth?.role ?? null;
    const orgUuid = me?.auth?.organization_uuid || null;
    if (!currentOrganization && orgUuid) {
      const { res, data: orgData } = await organizationsApi.list();
      if (res?.ok) {
        const orgs = Array.isArray(orgData?.items) ? orgData.items : [];
        currentOrganization =
          orgs.find((o) => o?.organization_uuid === orgUuid) || currentOrganization;
      }
    }
  }
  return { user, auth, currentOrganization, role };
}

/**
 * Sesión con usuario persistido (recarga/F5) + tokens en localStorage vía `api/client`.
 * Tras hidratar desde disco se llama `refreshSession` para validar el access token con `/me`.
 */
export const useAuthStore = create(
  persist(
    (set, _get) => ({
      user: null,
      auth: null,
      currentOrganization: null,
      role: null,
      loading: true,
      hasHydrated: false,

      refreshSession: async () => {
        set({ loading: true });
        const token = getStoredAccessToken();
        if (!token) {
          set({ user: null, auth: null, currentOrganization: null, role: null, loading: false });
          return;
        }
        try {
          const data = await apiGetMe();
          const auth = data?.auth ?? null;
          let currentOrganization = _get().currentOrganization;
          const orgUuid = auth?.organization_uuid || null;
          if (orgUuid) {
            const { res, data: orgData } = await organizationsApi.list();
            if (res?.ok) {
              const orgs = Array.isArray(orgData?.items) ? orgData.items : [];
              currentOrganization =
                orgs.find((o) => o?.organization_uuid === orgUuid) || null;
            } else {
              currentOrganization = null;
            }
          } else {
            currentOrganization = null;
          }
          set({ user: data?.user ?? null, auth, currentOrganization, role: auth?.role ?? null });
        } catch {
          clearStoredTokens();
          set({ user: null, auth: null, currentOrganization: null, role: null });
        } finally {
          set({ loading: false });
        }
      },

      login: async (email, password) => {
        set({ loading: true });
        try {
          const data = await apiLogin(email, password);
          // 2FA por correo: el backend pide un código antes de emitir tokens.
          if (data?.mfa_required) {
            return { mfaRequired: true, challenge: data.challenge };
          }
          const session = await resolveSessionFromData(data);
          set(session);
          return data;
        } finally {
          set({ loading: false });
        }
      },

      verifyLoginCode: async (challengeId, code) => {
        set({ loading: true });
        try {
          const data = await apiVerifyLoginCode(challengeId, code);
          const session = await resolveSessionFromData(data);
          set(session);
          return data;
        } finally {
          set({ loading: false });
        }
      },

      resendLoginCode: async (challengeId) => {
        const data = await apiResendLoginCode(challengeId);
        return data?.challenge || null;
      },

      logout: async () => {
        set({ loading: true });
        try {
          await apiLogout();
        } finally {
          set({ user: null, auth: null, currentOrganization: null, role: null, loading: false });
        }
      },
    }),
    {
      name: AUTH_PERSIST_KEY,
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        auth: state.auth,
        currentOrganization: state.currentOrganization,
        role: state.role,
      }),
      onRehydrateStorage: () => (_state, error) => {
        queueMicrotask(() => {
          if (error) {
            console.warn("[auth] Error al restaurar sesión desde disco:", error);
          }
          useAuthStore.setState({ hasHydrated: true });
          useAuthStore.getState().refreshSession();
        });
      },
    },
  ),
);
